import { Theme, alpha } from "@mui/material/styles";
import { getRelativeFontSize, mediumFont, regularFont } from "utils/styles";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

export const getContactStyles = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    root: {
      minHeight: "100vh",
      position: "relative" as const,
      overflow: "hidden",
      backgroundColor: isDark
        ? theme.palette.background.default
        : alpha("#eef0f5", 1),
      py: 8,
      px: { xs: 2, sm: 3 },
    },
    backgroundLayer: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: "hidden",
      pointerEvents: "none" as const,
      zIndex: 0,
    },
    gradientOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isDark
        ? `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(
            BRAND,
            0.18
          )} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, ${alpha(
            BRAND,
            0.12
          )} 0%, transparent 50%)`
        : `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(
            BRAND,
            0.12
          )} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, ${alpha(
            BRAND,
            0.08
          )} 0%, transparent 50%)`,
      zIndex: 0,
    },
    orb1: {
      position: "absolute" as const,
      top: "10%",
      right: "10%",
      width: 320,
      height: 320,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${alpha(
        BRAND,
        isDark ? 0.22 : 0.14
      )} 0%, transparent 65%)`,
      filter: "blur(50px)",
      animation: "contactPulse 8s ease-in-out infinite",
    },
    orb2: {
      position: "absolute" as const,
      bottom: "15%",
      left: "5%",
      width: 280,
      height: 280,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${alpha(
        BRAND,
        isDark ? 0.18 : 0.1
      )} 0%, transparent 65%)`,
      filter: "blur(45px)",
      animation: "contactPulse 9s ease-in-out infinite 2s",
    },
    gridOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `linear-gradient(${alpha(
        theme.palette.divider,
        isDark ? 0.1 : 0.12
      )} 1px, transparent 1px), linear-gradient(90deg, ${alpha(
        theme.palette.divider,
        isDark ? 0.1 : 0.12
      )} 1px, transparent 1px)`,
      backgroundSize: "40px 40px",
      opacity: 1,
    },
    headerBox: {
      position: "relative" as const,
      zIndex: 1,
      textAlign: "center" as const,
      py: { xs: 6, md: 8 },
    },
    headerBadge: {
      display: "inline-block",
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      color: BRAND,
      backgroundColor: alpha(BRAND, 0.1),
      px: 2,
      py: 0.75,
      borderRadius: "999px",
      mb: 2,
      border: `1px solid ${alpha(BRAND, 0.25)}`,
    },
    headerText: {
      ...mediumFont,
      fontSize: getRelativeFontSize(28),
      color: theme.palette.text.primary,
      lineHeight: 1.2,
      position: "relative" as const,
      display: "inline-block",
      "&::after": {
        content: '""',
        position: "absolute" as const,
        bottom: -12,
        left: "50%",
        transform: "translateX(-50%)",
        width: 64,
        height: 4,
        borderRadius: 2,
        background: `linear-gradient(90deg, ${BRAND}, ${BRAND_LIGHT})`,
      },
      [theme.breakpoints.down("md")]: {
        fontSize: getRelativeFontSize(22),
      },
    },
    subHeaderText: {
      ...regularFont,
      fontSize: getRelativeFontSize(2),
      color: theme.palette.text.secondary,
      mt: 3,
      maxWidth: 560,
      mx: "auto",
      lineHeight: 1.6,
    },
    contentWrapper: {
      position: "relative" as const,
      zIndex: 1,
    },
    section: {
      position: "relative" as const,
      zIndex: 1,
      mb: { xs: 6, md: 10 },
    },
    sectionNumber: {
      ...mediumFont,
      fontSize: getRelativeFontSize(0),
      color: BRAND,
      opacity: 0.9,
      mb: 1,
    },
    sectionTitle: {
      ...mediumFont,
      fontSize: getRelativeFontSize(10),
      color: theme.palette.text.primary,
      mb: 1,
      position: "relative" as const,
      display: "inline-block",
      "&::after": {
        content: '""',
        position: "absolute" as const,
        bottom: -6,
        left: 0,
        width: 48,
        height: 3,
        borderRadius: 2,
        background: `linear-gradient(90deg, ${BRAND}, ${BRAND_LIGHT})`,
      },
    },
    sectionSubtitle: {
      ...regularFont,
      fontSize: getRelativeFontSize(1),
      color: theme.palette.text.secondary,
      maxWidth: 520,
    },
    card: {
      borderRadius: "4px",
      overflow: "hidden" as const,
      background: theme.palette.background.paper,
      border: `1px solid ${
        isDark
          ? alpha(theme.palette.divider, 0.2)
          : alpha(theme.palette.divider, 0.25)
      }`,
      boxShadow: isDark
        ? `0 24px 64px ${alpha(theme.palette.common.black, 0.35)}`
        : `0 32px 64px ${alpha(theme.palette.common.black, 0.08)}`,
      position: "relative" as const,
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: 5,
        height: "100%",
        background: `linear-gradient(180deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
        borderRadius: "5px 0 0 5px",
      },
    },
    cardInner: {
      p: { xs: 3, sm: 4 },
      pl: { xs: 4, sm: 5 },
    },
    contactFormHeader: {
      ...mediumFont,
      fontSize: getRelativeFontSize(6),
      color: theme.palette.text.primary,
      mb: 0.5,
    },
    contactFormSubheader: {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      color: theme.palette.text.secondary,
      mb: 3,
    },
    formSectionLabel: {
      ...mediumFont,
      fontSize: getRelativeFontSize(1),
      color: theme.palette.text.secondary,
      mb: 1.5,
      display: "block",
    },
    formSectionBox: {
      p: 2,
      borderRadius: "4px",
      backgroundColor: alpha(theme.palette.divider, isDark ? 0.06 : 0.04),
      mb: 2,
    },
    submitRow: {
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      pt: 3,
      mt: 3,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderWidth: 1.5,
          borderColor: isDark
            ? alpha(theme.palette.divider, 0.5)
            : alpha(theme.palette.common.black, 0.22),
        },
        "&:hover fieldset": {
          borderColor: alpha(BRAND, 0.6),
        },
        "&.Mui-focused fieldset": {
          borderColor: BRAND,
          borderWidth: 2,
          boxShadow: `0 0 0 4px ${alpha(BRAND, 0.15)}`,
        },
      },
    },
    primaryButton: {
      minHeight: 48,
      py: 1.5,
      fontSize: "1rem",
    },
    detailCard: {
      borderRadius: "4px",
      p: 2.5,
      pl: 3,
      background: theme.palette.background.paper,
      border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.2 : 0.2)}`,
      height: "100%",
      transition: "border-color 0.2s, box-shadow 0.2s",
      position: "relative" as const,
      overflow: "hidden" as const,
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: 4,
        height: "100%",
        background: `linear-gradient(180deg, ${BRAND}, ${BRAND_LIGHT})`,
        borderRadius: "4px 0 0 4px",
      },
      "&:hover": {
        borderColor: alpha(BRAND, 0.4),
        boxShadow: `0 8px 24px ${alpha(BRAND, 0.08)}`,
      },
    },
    detailLabel: {
      ...mediumFont,
      fontSize: getRelativeFontSize(2),
      color: theme.palette.text.secondary,
      mb: 0.5,
    },
    detailValue: {
      ...regularFont,
      fontSize: getRelativeFontSize(1),
      color: theme.palette.text.primary,
    },
    detailLink: {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      color: BRAND,
      cursor: "pointer",
      mt: 1,
      "&:hover": {
        textDecoration: "underline",
      },
    },
    noteText: {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      color: theme.palette.text.secondary,
    },
    leftColumnTitle: {
      ...mediumFont,
      fontSize: getRelativeFontSize(3),
      color: theme.palette.text.primary,
      mb: 2,
    },
    websiteCard: {
      borderRadius: "4px",
      overflow: "hidden" as const,
      background: theme.palette.background.paper,
      border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.2 : 0.25)}`,
      height: "100%",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: alpha(BRAND, 0.4),
        boxShadow: `0 12px 32px ${alpha(
          theme.palette.common.black,
          isDark ? 0.25 : 0.08
        )}`,
      },
    },
    websiteCardImage: {
      width: "100%",
      height: 140,
      objectFit: "cover" as const,
      display: "block",
    },
    websiteCardLogoImage: {
      width: "100%",
      height: 140,
      objectFit: "contain" as const,
      display: "block",
      backgroundColor: isDark
        ? theme.palette.background.default
        : alpha(theme.palette.background.paper, 0.95),
      p: 1.5,
    },
    websiteCardBody: {
      p: 2,
    },
    websiteCardLogo: {
      height: 36,
      width: "auto",
      maxWidth: "100%",
      mb: 1,
      display: "block",
    },
    websiteCardTitle: {
      ...mediumFont,
      fontSize: getRelativeFontSize(4),
      color: theme.palette.text.primary,
      mb: 1,
    },
    websiteCardDescription: {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      color: theme.palette.text.secondary,
      lineHeight: 1.6,
    },
    websiteCardLink: {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      color: BRAND,
      cursor: "pointer",
      mt: 1.5,
      display: "inline-block",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    emailStrip: {
      borderRadius: "4px",
      overflow: "hidden" as const,
      position: "relative" as const,
      p: { xs: 3, md: 4 },
      background: isDark
        ? alpha(theme.palette.background.paper, 0.6)
        : alpha(theme.palette.background.paper, 0.95),
      border: `2px solid ${alpha(BRAND, 0.2)}`,
      boxShadow: isDark
        ? `0 16px 48px ${alpha(theme.palette.common.black, 0.3)}`
        : `0 16px 48px ${alpha(BRAND, 0.08)}`,
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${BRAND}, ${BRAND_LIGHT}, ${BRAND})`,
        backgroundSize: "200% 100%",
        animation: "contactPulse 6s ease-in-out infinite",
      },
    },
    emailStripInner: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 3,
    },
    emailStripIconWrap: {
      width: 64,
      height: 64,
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: `linear-gradient(135deg, ${alpha(BRAND, 0.15)} 0%, ${alpha(
        BRAND_LIGHT,
        0.1
      )} 100%)`,
      border: `1px solid ${alpha(BRAND, 0.25)}`,
    },
    emailStripLabel: {
      ...mediumFont,
      fontSize: getRelativeFontSize(2),
      color: theme.palette.text.secondary,
      mb: 0.5,
    },
    emailStripValue: {
      ...mediumFont,
      fontSize: getRelativeFontSize(4),
      color: theme.palette.text.primary,
      letterSpacing: "0.02em",
    },
    emailStripButton: {
      ...mediumFont,
      textTransform: "none",
      borderRadius: "12px",
      px: 3,
      py: 1.5,
      border: `2px solid ${BRAND}`,
      color: BRAND,
      "&:hover": {
        borderColor: BRAND_LIGHT,
        background: alpha(BRAND, 0.08),
      },
    },
  };
};

// Legacy default export for any remaining references
const contactStyle = {
  backImg: {},
  innerBackground: {},
  headerText: {},
  subHeaderText: {},
  calculatorHeader: {},
  contactFormHeader: {},
  contactButtonText: {},
  contactButtonTextHover: {},
  contactBtn: {},
  contactNoteText: {},
} as const;

export default contactStyle;
